import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SharedAccountNotice from "../SharedAccountNotice";
import ConnectionFlowMap from "./ConnectionFlowMap";
import SelectionPanel, { NavSection } from "../../common/configPanel/SelectionPanel";
import ActionSectionForm from "../../common/configPanel/ActionSectionForm";
import { useConfigEditor } from "../../common/configPanel/useConfigEditor";
import {
    actionIsUnsaved, actionNeedsSetup, hasFields, isAuth, isFlowAction, slugOf,
} from "../../common/configPanel/actionSetup";
import { actionLabel } from "../../common/configPanel/buildActionNav";
import { AccountBundle, FlowDirectionKey, Selection } from "./flowMapTypes";
import { buildFlowArrows, buildGraph, hasType, useAccountBundles } from "./useFlowGraph";
import { useTranslation } from "react-i18next";
import { getConnectionKind } from "../../common/connectionKind";
import { getEnvironment } from "../../common/getEnvironment";
import { CopyToClipboard, HideString, ShowString } from "../../common/ProjectIcons";
import { CodeSampleSwitcher } from "../codeSamples";
import ConnectionDetailsWebhook from "../ConnectionDetailsWebhook";
import { getDestinationAccount } from "../../destinations/DestinationHelperFunctions";
import { getReverseConnections } from "../../../api/connections/connectionsApi";
import { buttonClasses } from "../../common/tailwindClassnames";
import ModalShell from "../../common/ModalShell";
// The portal's canonical switch (headless-ui + the shared switchContainer classes),
// reused here so the direction toggle looks like every other toggle in the app.
import CustomCheckbox from "../../common/SchemaFormTemplates/CustomCheckbox";
import { ActionPillButton } from "../flowIcons";
import { Rule } from "../transformationRules/fieldMappingTypes";

export function useSelectionFromUrl(connectionId: string): {
    selection: Selection;
    select: (next: Selection) => void;
} {
    const navigate = useNavigate();
    // One splat route carries every selection depth:
    //   account/<integrationId>[/<section>]  |  flow/<destinationId>/<in|out>[/<section>]
    const splat = useParams()["*"] || "";
    const [kind, id, third, fourth] = splat.split("/").filter(Boolean);
    const base = `/connections/${connectionId}/configurations`;

    let selection: Selection = { kind: "none" };
    if (kind === "account" && id) {
        selection = { kind: "account", integrationId: id, section: third };
    } else if (kind === "flow" && id && (third === "in" || third === "out")) {
        selection = { kind: "flow", destinationId: id, direction: third as FlowDirectionKey, section: fourth };
    }

    const select = (next: Selection) => {
        if (next.kind === "none") return navigate(base);
        if (next.kind === "account")
            return navigate(`${base}/account/${next.integrationId}${next.section ? "/" + next.section : ""}`);
        return navigate(`${base}/flow/${next.destinationId}/${next.direction}${next.section ? "/" + next.section : ""}`);
    };

    return { selection, select };
}

// Technologies whose provider side is reached at a URL the user has to supply —
// Gundi cannot pull from an EarthRanger site it doesn't have the address of. Other
// provider types have a fixed endpoint baked into the connector.
const PROVIDER_TYPES_WITH_BASE_URL = ["earth_ranger", "cellstop", "mella_tracking"];

const ConfigurationsTab = (props: any) => {
    const { connection, connectionProvider, token, currentMemberIsAdmin, apiKey } = props;
    const { t } = useTranslation();
    const providerId: string = connectionProvider?.id || connection?.provider?.id;
    const destinations: any[] = React.useMemo(() => connection?.destinations || [], [connection?.destinations]);
    const accountIds = React.useMemo(
        () => [providerId, ...destinations.map((d: any) => d.id)].filter(Boolean),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [providerId, destinations.map((d: any) => d.id).join(",")]);

    // The parent fetches the provider integration itself — seed the bundle map
    // with it instead of re-fetching the same record.
    const { bundles, loading, failed, reload } = useAccountBundles(token, accountIds, providerId, connectionProvider);
    const { selection, select } = useSelectionFromUrl(connection?.id);

    const selectedAccountId = selection.kind === "account" ? selection.integrationId : undefined;
    const selectedArrowId = selection.kind === "flow" ? `${selection.destinationId}-${selection.direction}` : undefined;

    // Structural graph: every input is either a primitive or identity-stable
    // parent state, so a plain dependency array works. Selection is layered on
    // afterwards so clicking around never rebuilds nodes and edges.
    const structural = React.useMemo(() => {
        const built = buildFlowArrows({
            providerId,
            provider: connection?.provider,
            destinations,
            reverseRoutesBySite: props.reverseRoutesBySite || {},
            providerCanReceive: !!props.providerCanReceive,
            // undefined while the destination's integration hasn't loaded — the
            // arrow is drawn unavailable but claims no reason for it.
            destinationCanSend: (destId: string) => bundles[destId] ? hasType(bundles[destId].actions, "pull") : undefined,
            t,
        });

        // Checklist: an account/arrow "needs setup" while any of its actions with
        // required fields has no saved configuration. Optional-only actions don't
        // count — nothing blocks the flow without them.
        const needsConfig = (bundle: AccountBundle | undefined, pred: (a: any) => boolean) =>
            !!bundle && bundle.actions.some((a: any) => pred(a) && actionNeedsSetup(bundle, a));

        const needsSetupAccounts = new Set<string>();
        Object.values(bundles).forEach((b) => {
            if (needsConfig(b, (a: any) => !isFlowAction(a))) needsSetupAccounts.add(b.id);
        });
        // Webhook providers also need their payload parsing configured.
        if (connectionProvider?.type?.webhook && !connectionProvider?.webhook_configuration?.data) {
            needsSetupAccounts.add(providerId);
        }
        // So does a provider whose technology is reached at a URL the user supplies.
        if (PROVIDER_TYPES_WITH_BASE_URL.includes(connectionProvider?.type?.value)
            && !(bundles[providerId]?.base_url || connectionProvider?.base_url)) {
            needsSetupAccounts.add(providerId);
        }

        const needsSetupArrows = new Set<string>();
        built.forEach((arrow) => {
            if (arrow.state === "not-enableable") return;
            if (needsConfig(bundles[arrow.fromId], (a: any) => a.type === "pull")
                || needsConfig(bundles[arrow.toId], (a: any) => a.type === "push")) {
                needsSetupArrows.add(arrow.id);
            }
        });

        const graph = buildGraph({
            provider: connection?.provider,
            destinations,
            arrows: built,
            bundles,
            destinationsWithRules: props.destinationsWithRules || new Set<string>(),
            needsSetupAccounts,
            needsSetupArrows,
            t,
        });
        return {
            ...graph,
            arrows: built,
            needsSetupCount: needsSetupAccounts.size + needsSetupArrows.size,
            // Not just the account's general sections: a provider whose pull action
            // is unconfigured is not configured, even though that badge lives on an
            // arrow rather than on the node.
            // needsSetupAccounts is what the node's "!" badge reads, and it also
            // covers the two non-action gaps (webhook parsing, a provider URL the
            // user has to supply) — the banner has to agree with the badge, so it
            // asks the same set, plus the flow actions the badge leaves to arrows.
            // An account whose integration failed to load has no bundle, and
            // "couldn't be inspected" must not be reported as "configured" — the
            // unknown side counts as needing setup, which is what the load-failure
            // banner above the map is already telling the user to retry.
            providerNeedsSetup: !bundles[providerId]
                || needsSetupAccounts.has(providerId)
                || needsConfig(bundles[providerId], () => true),
            // Same question asked of the other side: an attached destination whose
            // own actions (general or flow) still have required fields is not a
            // finished step, even though the connection structurally has one.
            destinationsNeedSetup: destinations.some((d: any) =>
                !bundles[d.id] || needsConfig(bundles[d.id], () => true)),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerId, connection?.provider, connectionProvider, destinations, bundles,
        props.reverseRoutesBySite, props.providerCanReceive, props.destinationsWithRules, t]);

    const arrows = structural.arrows;

    // The Quick Start banner lives on the page above this tab and has no view of
    // the per-account configurations, so report what is still unconfigured.
    const { onNeedsSetupChange } = props;
    const needsSetupCount = loading ? null : structural.needsSetupCount;
    const providerNeedsSetup = structural.providerNeedsSetup;
    const destinationsNeedSetup = structural.destinationsNeedSetup;
    // Nothing loaded at all (no token yet, or it expired) is not a report: with an
    // empty bundle map every account would read as unconfigured and the banner
    // would flip a finished connection to all-warnings.
    const anyBundleLoaded = Object.keys(bundles).length > 0;
    useEffect(() => {
        if (needsSetupCount !== null && anyBundleLoaded) onNeedsSetupChange?.({
            total: needsSetupCount, provider: providerNeedsSetup, destinations: destinationsNeedSetup,
        });
    }, [needsSetupCount, providerNeedsSetup, destinationsNeedSetup, anyBundleLoaded, onNeedsSetupChange]);

    const { nodes, edges } = React.useMemo(() => ({
        nodes: structural.nodes.map((n: any) =>
            n.id === selectedAccountId ? { ...n, data: { ...n.data, isSelected: true } } : n),
        edges: structural.edges.map((e: any) =>
            e.id === selectedArrowId ? { ...e, data: { ...e.data, isSelected: true } } : e),
    }), [structural, selectedAccountId, selectedArrowId]);

    // Confirmations render through the shared ModalShell rather than window.confirm,
    // so they match every other dialog in the portal.
    type Confirmation = {
        title: string; body: string; confirmLabel: string; destructive?: boolean; onConfirm: () => void;
    };
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

    // ---- unsaved edits, stashed per account and PATCHed on Save -------------
    // Shared with every other configuration screen (destination page, wizard) so
    // "Save" always means "PATCH this one section".
    const editor = useConfigEditor({ token, t, onSaved: (id: string) => reload(id) });
    const { dirtyIds, anyDirty } = editor;

    // Switching to a different node/arrow throws away whatever is stashed for the
    // current one, so ask first. Switching sections inside the same selection keeps
    // the same pending stash and never prompts.
    const guardedSelect = (next: Selection) => {
        const sameAccount = next.kind === "account" && selection.kind === "account" && next.integrationId === selection.integrationId;
        const sameFlow = next.kind === "flow" && selection.kind === "flow"
            && next.destinationId === selection.destinationId && next.direction === selection.direction;
        // The provider's URL draft is unsaved state too, even though it is not an
        // action configuration.
        if ((anyDirty || baseUrlDraft !== null) && !sameAccount && !sameFlow) {
            return setConfirmation({
                title: t("Discard unsaved changes?"),
                body: t("This selection has edits that haven't been saved. Moving to another account or flow discards them."),
                confirmLabel: t("Discard changes"),
                destructive: true,
                onConfirm: () => { editor.discardAll(); setBaseUrlDraft(null); select(next); },
            });
        }
        select(next);
    };

    // ---- other connections sharing a destination account -------------------
    // Fetched lazily for the destination whose panel is open (the notice only
    // renders there), and cached per id — never one call per destination upfront.
    const [sharedBySite, setSharedBySite] = useState<Record<string, { id: string, name: string }[]>>({});
    const sharedFetched = useRef<Set<string>>(new Set());
    const selectedDestId = selection.kind === "account" && selection.integrationId !== providerId
        ? selection.integrationId : undefined;
    useEffect(() => {
        if (!token || !selectedDestId || sharedFetched.current.has(selectedDestId)) return;
        sharedFetched.current.add(selectedDestId);
        let cancelled = false;
        (async () => {
            try {
                const data = await getReverseConnections(token, selectedDestId);
                const others = (data?.results || data || [])
                    .filter((c: any) => c?.id && c.id !== connection?.id)
                    // No name fallback to the raw id: a connection the user can't
                    // resolve renders as a masked count, never as an identifier.
                    .map((c: any) => ({ id: c.id, name: c.provider?.name }));
                if (!cancelled) setSharedBySite((prev) => ({ ...prev, [selectedDestId]: others }));
            } catch (e) {
                // Allow a retry on the next visit to this panel.
                sharedFetched.current.delete(selectedDestId);
                console.error("failed to load connections sharing site", selectedDestId, e);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, selectedDestId, connection?.id]);

    // ---- section content ---------------------------------------------------
    const renderActionForm = (bundle: AccountBundle, action: any, editable: boolean) => (
        <ActionSectionForm
            target={bundle}
            action={action}
            editable={editable}
            userIsAdmin={currentMemberIsAdmin}
            token={token}
            formVersion={editor.formVersion[bundle.id] || 0}
            errors={editor.formErrors[bundle.id]?.[action.id]}
            formData={editor.formDataFor(bundle, action)}
            onChange={(data: any, fieldId?: string) => editor.snapshotChange(bundle, action.id, data, fieldId)}
            test={props.onTestAction
                ? { onTestAction: props.onTestAction, loading: props.testActionLoading, response: props.testActionResponse }
                : undefined}
        />
    );

    // Field mapping lives on the route (per flow), not on the account, and is
    // edited in the existing transformation-rules side panel.
    const fieldMappingSection = (dest: any) => {
        const rule: Rule | null = dest ? props.getRule(dest.id) : null;
        if (!rule) return <ActionPillButton label={t("Add Field Mapping")} onClick={() => props.onOpenRules(dest)} />;
        const mapEntries = rule.mapping.map ? Object.entries(rule.mapping.map) : [];
        return (
            <>
                <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-semibold text-off-black">{t("Field mapping")}</span>
                    <button className="ml-auto text-connection-green text-sm font-semibold underline underline-offset-2"
                        onClick={() => props.onOpenRules(dest)}>
                        {t("Edit")} ▸
                    </button>
                </div>
                <div className="rounded border border-gray-lines bg-secondary-background px-4 py-3 text-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="bg-light-green text-connection-green rounded px-2 py-0.5 text-xs font-semibold">
                            {rule.streamType === "ev" ? t("Events") : t("Observations")}
                        </span>
                        <span className="font-mono text-xs text-off-black">
                            {rule.mapping.provider_field || t("(source value)")} → {rule.mapping.destination_field}
                        </span>
                    </div>
                    {mapEntries.length > 0 && (
                        <div className="flex flex-col gap-1">
                            {mapEntries.slice(0, 4).map(([from, to]) => (
                                <div key={from} className="flex items-center gap-2 font-mono text-xs text-secondary-text">
                                    <span className="bg-white border border-gray-lines rounded px-2 py-0.5">{from}</span>
                                    <span className="text-connection-green font-bold">→</span>
                                    <span className="bg-white border border-gray-lines rounded px-2 py-0.5">{String(to)}</span>
                                </div>
                            ))}
                            {mapEntries.length > 4 && (
                                <span className="text-xs text-secondary-text">{t("+ {{count}} more…", { count: mapEntries.length - 4 })}</span>
                            )}
                        </div>
                    )}
                    {rule.mapping.default !== undefined && (
                        <div className="font-mono text-xs text-secondary-text mt-1">{t("default")}: {String(rule.mapping.default)}</div>
                    )}
                </div>
            </>
        );
    };

    // ---- how to connect (provider API key + endpoint) -----------------------
    // Push/webhook providers are configured by POSTing to Gundi, so their panel
    // leads with the endpoint + API key that used to live on the Provider tab.
    const [keyVisible, setKeyVisible] = useState(false);
    // Draft of the provider's base URL; null means "not edited".
    const [baseUrlDraft, setBaseUrlDraft] = useState<string | null>(null);
    const [savingBaseUrl, setSavingBaseUrl] = useState(false);
    // Same rule the destination form applies: a URL Gundi can actually call.
    const baseUrlError = baseUrlDraft === null ? undefined : (() => {
        const value = baseUrlDraft.trim();
        if (value.length === 0) return t("URL is required.");
        try { new URL(value); return undefined; } catch { return t("Enter a valid URL including the scheme."); }
    })();
    const copyText = (text: string, label: string) =>
        navigator.clipboard.writeText(text)
            .then(() => toast.info(t("{{label}} copied to clipboard", { label })))
            .catch(() => toast.error(t("Failed to copy to clipboard")));

    const howToConnectSection = (kind: string) => {
        const rawEnv = getEnvironment();
        const env = rawEnv === "local" ? "dev" : rawEnv;
        const endpoint = kind === "webhook"
            ? (env === "production" ? "https://hooks.gundiservice.org/webhooks" : `https://hooks.${env}.gundiservice.org/webhooks`)
            : (env === "production" ? "https://sensors.api.gundiservice.org/v2/observations/" : `https://sensors.api.${env}.gundiservice.org/v2/observations/`);
        const integrationType = connectionProvider?.type?.value || "";
        return (
            <div className="flex flex-col gap-4">
                <div className="rounded border border-field-outline bg-white p-4">
                    <p className="text-sm text-off-black mb-4">
                        {kind === "webhook"
                            ? t("Configure your source system to POST to the Gundi webhook endpoint below. Include your API key and the integration-type header with every request.")
                            : t("Send data to the Gundi observations endpoint below, including your API key with every request.")}
                    </p>
                    <div className="rounded border border-field-outline bg-very-light-gray overflow-hidden">
                        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-divider-lines">
                            <div className="flex flex-col min-w-0">
                                <span className="text-xs text-secondary-gray mb-0.5">{t("Endpoint")}</span>
                                <span className="font-mono text-sm text-off-black truncate">{endpoint}</span>
                            </div>
                            <button type="button" onClick={() => copyText(endpoint, t("Endpoint"))}
                                className="text-secondary-gray hover:text-off-black flex-shrink-0" aria-label={t("Copy endpoint")}>
                                <CopyToClipboard />
                            </button>
                        </div>
                        {kind === "webhook" && (
                            <div className="grid grid-cols-2 border-b border-divider-lines bg-very-light-gray px-4 py-2">
                                <span className="text-xs text-secondary-gray">{t("Header")}</span>
                                <span className="text-xs text-secondary-gray">{t("Value")}</span>
                            </div>
                        )}
                        <div className={`${kind === "webhook" ? "grid grid-cols-2" : "flex items-center justify-between gap-4"} border-b border-divider-lines`}>
                            {kind === "webhook" ? (
                                <div className="flex items-center justify-between gap-2 px-4 py-3 border-r border-divider-lines">
                                    <span className="font-mono text-sm text-off-black">apikey</span>
                                    <button type="button" onClick={() => copyText("apikey", t("Header name"))}
                                        className="text-secondary-gray hover:text-off-black flex-shrink-0" aria-label={t("Copy header name")}>
                                        <CopyToClipboard />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col min-w-0 px-4 py-3">
                                    <span className="text-xs text-secondary-gray mb-0.5">{t("API key")}</span>
                                    <span className="font-mono text-sm text-off-black truncate">
                                        {apiKey ? (keyVisible && currentMemberIsAdmin ? apiKey : "••••••••••••••••") : t("Loading…")}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center justify-between gap-2 px-4 py-3">
                                {kind === "webhook" && (
                                    <span className="font-mono text-sm text-off-black flex-1 truncate">
                                        {apiKey ? (keyVisible && currentMemberIsAdmin ? apiKey : "••••••••••••••••") : t("Loading…")}
                                    </span>
                                )}
                                {/* Revealing and copying the key stays an admin action, as it
                                    was on the provider tab this panel replaced. */}
                                {currentMemberIsAdmin && (
                                    <span className="flex items-center gap-2 flex-shrink-0 ml-auto">
                                        <button type="button" onClick={() => setKeyVisible((v) => !v)}
                                            className="text-secondary-gray hover:text-off-black" aria-label={keyVisible ? t("Hide API key") : t("Show API key")}>
                                            {keyVisible ? <HideString /> : <ShowString />}
                                        </button>
                                        <button type="button" onClick={() => apiKey && copyText(apiKey, t("API key"))}
                                            className="text-secondary-gray hover:text-off-black" aria-label={t("Copy API key")}>
                                            <CopyToClipboard />
                                        </button>
                                    </span>
                                )}
                            </div>
                        </div>
                        {kind === "webhook" && (
                            <div className="grid grid-cols-2">
                                <div className="flex items-center justify-between gap-2 px-4 py-3 border-r border-divider-lines">
                                    <span className="font-mono text-sm text-off-black">x-gundi-integration-type</span>
                                    <button type="button" onClick={() => copyText("x-gundi-integration-type", t("Header name"))}
                                        className="text-secondary-gray hover:text-off-black flex-shrink-0" aria-label={t("Copy header name")}>
                                        <CopyToClipboard />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between gap-2 px-4 py-3">
                                    <span className="font-mono text-sm text-off-black truncate">{integrationType}</span>
                                    <button type="button" onClick={() => copyText(integrationType, t("Header value"))}
                                        className="text-secondary-gray hover:text-off-black flex-shrink-0" aria-label={t("Copy header value")}>
                                        <CopyToClipboard />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="rounded border border-field-outline bg-white p-4">
                    <p className="text-sm text-off-black mb-4">
                        {kind === "webhook"
                            ? t("Share this sample code with any developers integrating with this connection. Every request must include the apikey and x-gundi-integration-type headers, a timestamp preferably in ISO 8601 format, latitude and longitude, and a device or source ID.")
                            : t("Share this sample code and API key with any developers who need to push data to this connection. They can use it as a starting point to integrate their system directly.")}
                    </p>
                    <CodeSampleSwitcher
                        kind={kind === "webhook" ? "webhook" : "api-push"}
                        endpoint={endpoint}
                        apiKey={keyVisible && currentMemberIsAdmin ? apiKey : null}
                        integrationType={integrationType}
                        height={kind === "webhook" ? "280px" : "240px"}
                    />
                    <p className="text-sm text-off-black mt-4">
                        <a href={kind === "webhook"
                            ? "https://support.earthranger.com/developer_docs/custom-webhook"
                            : "https://support.earthranger.com/developer_docs/gundi-api"}
                            target="_blank" rel="noreferrer" className="text-blue-basic underline">
                            {kind === "webhook" ? t("Learn more about Gundi's webhook API.") : t("Learn more about Gundi's API.")}
                        </a>
                    </p>
                </div>
            </div>
        );
    };

    const accountPanel = () => {
        if (selection.kind !== "account") return null;
        const bundle = bundles[selection.integrationId];
        if (!bundle) {
            return (
                <div className="border border-gray-lines rounded-lg bg-white p-5">
                    {[0, 1, 2].map((i) => <div key={i} className="h-4 w-48 bg-neutral-gray-50 rounded animate-pulse mb-3" />)}
                </div>
            );
        }
        const dest = destinations.find((d: any) => d.id === bundle.id);

        // Pull/push configuration belongs to the arrows, so an account only shows
        // its credentials and its other (non-directional) actions.
        const generalActions = bundle.actions.filter((a) => !isFlowAction(a) && hasFields(a));
        const ordered = [...generalActions].sort((a, b) => Number(isAuth(b)) - Number(isAuth(a)));
        const nav: NavSection[] = [];
        if (ordered.length > 0) {
            nav.push({
                cat: t("GENERAL"),
                items: ordered.map((a) => ({
                    slug: slugOf(a),
                    label: actionLabel(a, t),
                    needsSetup: actionNeedsSetup(bundle, a),
                })),
            });
        }
        if (!bundle.isProvider) nav.push({ cat: t("RULES"), items: [{ slug: "field-mapping", label: t("Field mapping") }] });
        const providerType = bundle.isProvider ? connectionProvider?.type : undefined;
        // Webhook providers keep the webhook endpoint + headers; everything else
        // gets the sensors endpoint. A type that carries a webhook is a webhook
        // here even when it also has pull config (kind would say "standard").
        const connectionKind = providerType && (getConnectionKind(providerType) === "webhook" || providerType.webhook)
            ? "webhook" : "api-push";
        // Webhook payload parsing is provider-side config, so it lives on this
        // panel (it used to be its own tab).
        const webhookConfig = bundle.isProvider && (connectionProvider?.webhook_configuration || connectionProvider?.type?.webhook)
            ? (connectionProvider.webhook_configuration ?? {
                id: "", integration: connectionProvider.id,
                webhook: connectionProvider.type?.webhook ?? { id: "", name: "", value: "" },
                data: null,
            })
            : undefined;
        if (webhookConfig) {
            nav.push({
                cat: t("WEBHOOK"),
                items: [{ slug: "webhook", label: t("Payload parsing"), needsSetup: !connectionProvider?.webhook_configuration?.data }],
            });
        }
        // Last section on the provider, and always present: whatever the
        // integration method, the connection has an API key its developers may need.
        if (providerType) {
            nav.push({ cat: t("CONNECT"), items: [{ slug: "how-to-connect", label: t("API key") }] });
        }

        // base_url lives on the integration record, not in an action configuration,
        // so it gets a section of its own rather than a form section.
        const showBaseUrl = bundle.isProvider
            && PROVIDER_TYPES_WITH_BASE_URL.includes(connectionProvider?.type?.value);
        if (showBaseUrl) {
            const endpointItem = { slug: "endpoint", label: t("Endpoint"), needsSetup: !bundle.base_url };
            // base_url lives on the integration record rather than in an action
            // configuration, but to the user it is one more thing to set on this
            // account — so it heads the GENERAL list instead of standing alone.
            const general = nav.find((section) => section.cat === t("GENERAL"));
            if (general) general.items = [endpointItem, ...general.items];
            else nav.unshift({ cat: t("GENERAL"), items: [endpointItem] });
        }

        const activeSlug = selection.section || nav[0]?.items[0]?.slug || (bundle.isProvider ? undefined : "field-mapping");
        const activeAction = ordered.find((a) => slugOf(a) === activeSlug);
        const healthy = String(bundle.status || "").toLowerCase() === "healthy";
        const isDirty = !!dirtyIds[bundle.id];
        // A never-saved action can be saved as-is, so Save isn't dead until the
        // user pokes a field.
        const canCommitDefaults = !!activeAction && actionIsUnsaved(bundle, activeAction);
        // EarthRanger only: the ER account (username or truncated token) this
        // destination authenticates with — the bundle already has the configs.
        const erAccount = dest ? getDestinationAccount({ ...dest, configurations: bundle.configurations } as any) : null;

        return (
            <SelectionPanel
                title={bundle.name}
                subtitle={<span className="flex items-center gap-2">
                    · {bundle.base_url || t("No URL")}
                    {erAccount && <span className="font-mono">· {erAccount}</span>}
                    <span className={`w-2 h-2 rounded-full ${healthy ? "bg-connection-green" : "bg-medium-gray"}`} title={bundle.status || t("unknown")} />
                </span>}
                headerActions={<>
                    {!bundle.isProvider && dest && (
                        <button className={buttonClasses.lightRed}
                            onClick={() => setConfirmation({
                                title: t("Remove from connection"),
                                body: t("Remove {{name}} from this connection? The site itself is not deleted — it just stops receiving data from this flow.", { name: bundle.name }),
                                confirmLabel: t("Remove"),
                                destructive: true,
                                onConfirm: () => { Promise.resolve(props.onRemoveSite(dest)).then(() => select({ kind: "none" })); },
                            })}>
                            {t("Remove from connection")}
                        </button>
                    )}
                </>}
                // The shared-account warning is about config stored on the account.
                // Field mappings live on this connection's route, so the notice
                // would be plainly wrong in that section.
                banner={activeSlug === "field-mapping" ? undefined : <SharedAccountNotice
                    accountName={bundle.name}
                    usedBy={bundle.isProvider ? (props.providerSharedWith || []) : (sharedBySite[bundle.id] || [])} />}
                nav={nav}
                activeSlug={activeSlug}
                onSelectSection={(slug) => select({ kind: "account", integrationId: bundle.id, section: slug })}
                emptyNavNote={t("No general configuration — pull and push settings live on the arrows.")}
                footer={activeSlug === "endpoint" && showBaseUrl ? <>
                    <button className={buttonClasses.primary}
                        disabled={baseUrlDraft === null || !!baseUrlError
                            || baseUrlDraft.trim() === (bundle.base_url ?? "") || savingBaseUrl}
                        onClick={async () => {
                            setSavingBaseUrl(true);
                            try {
                                // Keep the draft when the save didn't land, so the user's
                                // typing isn't silently replaced by the stored value.
                                const ok = await props.onSaveProviderBaseUrl?.((baseUrlDraft ?? "").trim());
                                if (ok !== false) setBaseUrlDraft(null);
                            } finally { setSavingBaseUrl(false); }
                        }}>{t("Save")}</button>
                    <button className={buttonClasses.cancel} disabled={baseUrlDraft === null}
                        onClick={() => setBaseUrlDraft(null)}>{t("Cancel")}</button>
                </> : activeAction ? <>
                    <button className={buttonClasses.primary} disabled={!isDirty && !canCommitDefaults}
                        onClick={() => { editor.commitDefaults(bundle, activeAction); editor.save(bundle); }}>{t("Save")}</button>
                    <button className={buttonClasses.cancel} disabled={!isDirty} onClick={() => editor.cancel(bundle)}>{t("Cancel")}</button>
                </> : undefined}>
                {activeSlug === "endpoint" && showBaseUrl
                    ? <div className="rounded border border-field-outline bg-white p-4">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                            <div className="md:w-1/2 flex flex-col">
                                <label className="mr-1">{t("URL")} *</label>
                                <span className="text-sm text-secondary-gray mt-1">{t("The address Gundi uses to reach this system.")}</span>
                            </div>
                            <div className="md:w-1/2">
                                <input
                                    id="provider_base_url"
                                    value={baseUrlDraft ?? bundle.base_url ?? ""}
                                    onChange={(e) => setBaseUrlDraft(e.target.value)}
                                    className={`w-full p-1.5 border rounded px-2 transition-colors duration-300 ${
                                        baseUrlError ? "border-orange" : "border-neutral-gray-200"}`}
                                />
                                {baseUrlError && <p className="text-orange-600 text-sm mt-1">{baseUrlError}</p>}
                            </div>
                        </div>
                      </div>
                    : activeSlug === "how-to-connect"
                    ? howToConnectSection(connectionKind)
                    : activeSlug === "webhook" && webhookConfig
                        ? <ConnectionDetailsWebhook
                            webhookConfiguration={webhookConfig}
                            currentMemberIsAdmin={currentMemberIsAdmin}
                            onSave={props.onSaveWebhook}
                            onCancel={props.onCancelWebhook}
                            changesMade={props.webhookChangesMade}
                            onChangesMade={props.onWebhookChangesMade}
                          />
                        : activeAction
                            ? renderActionForm(bundle, activeAction, true)
                            : activeSlug === "field-mapping"
                                ? fieldMappingSection(dest)
                                : <p className="text-secondary-text text-sm">{t("This account has no general configuration.")}</p>}
            </SelectionPanel>
        );
    };

    const flowPanel = () => {
        if (selection.kind !== "flow") return null;
        const arrow = arrows.find((a) => a.id === `${selection.destinationId}-${selection.direction}`);
        const providerBundle = bundles[providerId];
        const destBundle = bundles[selection.destinationId];
        const dest = destinations.find((d: any) => d.id === selection.destinationId);
        if (!arrow || !providerBundle || !destBundle) {
            return (
                <div className="border border-gray-lines rounded-lg bg-white p-5">
                    {[0, 1, 2].map((i) => <div key={i} className="h-4 w-48 bg-neutral-gray-50 rounded animate-pulse mb-3" />)}
                </div>
            );
        }

        const sender = arrow.direction === "out" ? providerBundle : destBundle;
        const receiver = arrow.direction === "out" ? destBundle : providerBundle;
        const senderPulls = sender.actions.filter((a: any) => a.type === "pull" && hasFields(a));
        const receiverPushes = receiver.actions.filter((a: any) => a.type === "push" && hasFields(a));
        const nav: NavSection[] = [];
        // The nav headers name the technology, not the account: "FROM EARTHRANGER"
        // reads as the kind of system data comes from, and account names are
        // already in the panel title.
        const techOf = (b: AccountBundle) => b.techName || b.name;
        if (senderPulls.length) nav.push({ cat: t("FROM {{name}}", { name: techOf(sender) }).toUpperCase(), items: senderPulls.map((a: any) => ({ slug: `from-${slugOf(a)}`, label: actionLabel(a, t), needsSetup: actionNeedsSetup(sender, a) })) });
        if (receiverPushes.length) nav.push({ cat: t("TO {{name}}", { name: techOf(receiver) }).toUpperCase(), items: receiverPushes.map((a: any) => ({ slug: `to-${slugOf(a)}`, label: actionLabel(a, t), needsSetup: actionNeedsSetup(receiver, a) })) });

        const activeSlug = selection.section || nav[0]?.items[0]?.slug;
        const fromSender = !!activeSlug?.startsWith("from-");
        const owner = fromSender ? sender : receiver;
        const activeAction = (fromSender ? senderPulls : receiverPushes)
            .find((a: any) => `${fromSender ? "from" : "to"}-${slugOf(a)}` === activeSlug);
        const editable = arrow.state !== "not-enableable";
        // Same as the account panel: an action that was never saved can be committed
        // with its defaults, so Save is live even before the user edits anything.
        const canCommitDefaults = !!activeAction && editable && actionIsUnsaved(owner, activeAction);
        // A flow panel edits two accounts (sender pulls, receiver pushes), and
        // section switches between them don't prompt — so Save/Cancel must cover
        // whichever sides are dirty, not just the active section's owner.
        const dirtyBundles = [sender, receiver].filter((b) => dirtyIds[b.id]);
        const isDirty = dirtyBundles.length > 0;

        const headerActions = arrow.state === "enabled" && arrow.direction === "in" ? (
            <span className="flex items-center gap-2">
                <span className="text-xs text-secondary-text">{t("Sending")}</span>
                <CustomCheckbox label={t("Sending")} formData={true} onChange={() => dest && setConfirmation({
                    title: t("Stop sending data"),
                    body: t("Stop sending data from {{from}} to {{to}}? This removes the reverse route; the configuration itself is kept.", { from: destBundle.name, to: providerBundle.name }),
                    confirmLabel: t("Stop sending"),
                    destructive: true,
                    onConfirm: () => { Promise.resolve(props.onDisableReverseFlow(dest)).then(() => props.onReloadConnection()); },
                })} />
            </span>
        ) : arrow.state === "disabled" ? (
            <span className="flex items-center gap-2">
                <span className="text-xs text-secondary-text">{t("Enable")}</span>
                <CustomCheckbox label={t("Enable")} formData={false} onChange={() => {
                    if (dest) Promise.resolve(props.onEnableReverseFlow(dest)).then(() => props.onReloadConnection());
                }} />
            </span>
        ) : undefined;

        const note = arrow.state === "disabled"
            ? t("Not enabled yet. Configure the sections below, then turn this direction on.")
            : undefined;

        return (
            <SelectionPanel
                title={`${sender.name} → ${receiver.name}`}
                subtitle={note ? <span className="text-xs text-secondary-text">{note}</span> : undefined}
                headerActions={headerActions}
                banner={arrow.state === "not-enableable" && arrow.reason
                    // Same amber treatment as SharedAccountNotice — it's a warning, not a neutral note.
                    ? <div className="mb-5 rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-off-black">{arrow.reason}</div>
                    : undefined}
                nav={nav}
                activeSlug={activeSlug}
                onSelectSection={(slug) => select({ kind: "flow", destinationId: selection.destinationId, direction: selection.direction, section: slug })}
                emptyNavNote={t("Nothing to configure for this direction.")}
                footer={activeAction && editable ? <>
                    <button className={buttonClasses.primary} disabled={!isDirty && !canCommitDefaults}
                        onClick={() => {
                            editor.commitDefaults(owner, activeAction);
                            const targets = dirtyBundles.some((b) => b.id === owner.id) || !canCommitDefaults
                                ? dirtyBundles : [...dirtyBundles, owner];
                            return Promise.all(targets.map((b) => editor.save(b)));
                        }}>{t("Save")}</button>
                    <button className={buttonClasses.cancel} disabled={!isDirty}
                        onClick={() => dirtyBundles.forEach((b) => editor.cancel(b))}>{t("Cancel")}</button>
                </> : undefined}>
                {activeAction
                    ? <div className={editable ? "" : "opacity-70 pointer-events-none"}>{renderActionForm(owner, activeAction, editable)}</div>
                    : <p className="text-secondary-text text-sm">
                        {nav.length === 0
                            ? t("This flow doesn't need any configuration — data moves on its own.")
                            : t("Select a section to configure.")}
                      </p>}
            </SelectionPanel>
        );
    };

    return (
        <div className="mt-8 px-4 pb-28">
            {failed && (
                <div className="mb-4 rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-off-black flex flex-wrap items-center gap-3">
                    <span>{t("Some accounts in this connection couldn't be loaded, so parts of the map may be incomplete.")}</span>
                    <button type="button" className={`${buttonClasses.lightGreen} ml-auto`} onClick={() => reload()}>
                        {t("Retry")}
                    </button>
                </div>
            )}
            <ConnectionFlowMap
                nodes={nodes}
                edges={edges}
                destinationCount={destinations.length}
                connectionStatus={connection?.status}
                connectionEnabled={connectionProvider?.enabled !== false}
                onSelectAccount={(id: string) => guardedSelect({ kind: "account", integrationId: id })}
                onSelectFlow={(destinationId: string, direction: FlowDirectionKey) => guardedSelect({ kind: "flow", destinationId, direction })}
                onAddDestination={props.onAddDestination}
                highlightAddDestination={!!props.highlightAddDestination}
            />
            {destinations.length === 0 && (
                <p className="text-secondary-text text-sm mb-6">{t("No destinations yet. Use the “Add destination” button to connect one.")}</p>
            )}
            {selection.kind === "none" && (
                <p className="text-secondary-text text-sm">
                    {loading ? t("Loading accounts…") : t("Select an account or a flow above to configure it.")}
                </p>
            )}
            {confirmation && (
                <ModalShell
                    title={confirmation.title}
                    onClose={() => setConfirmation(null)}
                    footer={<>
                        <button type="button"
                            className={confirmation.destructive ? buttonClasses.destructive : buttonClasses.primary}
                            onClick={() => { const run = confirmation.onConfirm; setConfirmation(null); run(); }}>
                            {confirmation.confirmLabel}
                        </button>
                        <button type="button" className={buttonClasses.cancel} onClick={() => setConfirmation(null)}>
                            {t("Cancel")}
                        </button>
                    </>}>
                    <p className="text-base text-off-black">{confirmation.body}</p>
                </ModalShell>
            )}
            {selection.kind === "account" && accountPanel()}
            {selection.kind === "flow" && flowPanel()}
        </div>
    );
};

export default ConfigurationsTab;
